import { useTranslation } from 'react-i18next'

import { Box, Dropdown, TextButton, IconButton, Text, Checkbox, Icon } from 'components'
import { DropdownItem } from 'components/Dropdown/types'

import { FixMeAny } from 'types/global'
import { sentenceFormat } from 'utils/string'
import { useMemo } from 'react'

export type InputAddDropdownValue = {
  dropdownItem: DropdownItem<string> | null
  isActive?: boolean
  checked?: boolean
}

interface Props {
  name: string
  label?: string
  options: DropdownItem<FixMeAny>[]
  values: Array<InputAddDropdownValue | null>
  onChange?: (values: Array<InputAddDropdownValue | null>) => void
  isDisabled?: boolean
  isEditable?: boolean
  isLoading?: boolean
  isUniqueSelection?: boolean
  hasCheckbox?: boolean
  checkboxLabel?: string
  isCheckboxDisabled?: boolean
  isDefaultChecked?: boolean
}

const InputAddDropdown = ({
  name,
  label,
  values = [null],
  options,
  onChange,
  isDisabled,
  isEditable = true,
  isLoading,
  isUniqueSelection = true,
  hasCheckbox = false,
  checkboxLabel = '',
  isCheckboxDisabled = false,
  isDefaultChecked = false,
}: Props) => {
  const { t } = useTranslation()

  const handleChange = (index: number) => (selected: DropdownItem<FixMeAny>) => {
    const list = values.slice()
    if (!list[index]) {
      list[index] = { dropdownItem: selected, checked: isDefaultChecked }
      onChange?.(list)
      return
    }
    list[index] = { dropdownItem: selected, checked: list[index]?.checked ?? isDefaultChecked }
    onChange?.(list)
  }

  const handleCheck = (index: number) => (checked: boolean) => {
    const list = values.slice()
    if (!list[index]) {
      list[index] = { dropdownItem: null, checked }
      onChange?.(list)
      return
    }
    list[index] = { dropdownItem: list[index]?.dropdownItem ?? null, checked }
    onChange?.(list)
  }

  const handleRemove = (index: number) => {
    const list = values.slice()
    list.splice(index, 1)
    onChange?.(list)
  }

  const hasValue = useMemo(() => values.some((i) => i?.dropdownItem), [values])

  if (!isEditable && !hasValue) return <></>

  return (
    <Box display="flex" flexDirection="column" gap="1rem" padding="1rem">
      {label && (
        <Text variant="subtitle" isCapitalizedFirstLetter>
          {label}
        </Text>
      )}
      {values?.map((i, idx) => {
        const otherValues = values.filter((_, j) => j !== idx).map((v) => v?.dropdownItem)

        const filteredOptions = options.filter((o) => {
          if (isUniqueSelection) {
            return !otherValues.find((i) => i?.value === o.value)
          }
          return true
        })

        const { checked } = i && typeof i === 'object' ? i : { checked: isDefaultChecked }

        const selectedOption = i?.dropdownItem
          ? {
              label: i.dropdownItem.label
                ? `${i.dropdownItem.label} ${!i.isActive ? `(${t('deactivated')})` : ''}`
                : '-',
              value: i.dropdownItem.value ?? '',
            }
          : null

        return (
          <Box key={idx} display="flex" alignItems="center" gap="1rem">
            {isEditable ? (
              <>
                <Box flex="1">
                  <Dropdown
                    name={`${name}-${idx}`}
                    placeholder={isDisabled ? '' : t('select_placeholder', { name: t(name) })}
                    withNoneOption
                    options={filteredOptions}
                    value={selectedOption}
                    onChange={handleChange(idx)}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                  />
                </Box>
                {hasCheckbox && (
                  <Checkbox
                    inline
                    name={checkboxLabel}
                    checked={checked}
                    onChange={handleCheck(idx)}
                    disabled={isCheckboxDisabled}
                  >
                    {checkboxLabel}
                  </Checkbox>
                )}
                {!isDisabled && (
                  <IconButton
                    variant="ghost"
                    icon={['far', 'trash-alt']}
                    iconColor="red"
                    onClick={() => handleRemove(idx)}
                  />
                )}
              </>
            ) : (
              <>
                {selectedOption && (
                  <Box display="flex" gap="1rem" alignItems="center" pl="3rem">
                    <Box display="flex" gap="0.5rem" width="5rem">
                      {hasCheckbox && checked && (
                        <>
                          <Icon color="green2" icon={['fa', 'check']} />
                          <Text color="green2">{checkboxLabel}</Text>
                        </>
                      )}
                    </Box>
                    <Text color={i?.isActive === false ? 'red' : 'black'}>{selectedOption?.label}</Text>
                  </Box>
                )}
              </>
            )}
          </Box>
        )
      })}

      {!isDisabled && isEditable && (
        <TextButton icon={['fas', 'plus']} onClick={() => onChange?.([...values, null])}>
          {sentenceFormat(t('add_more_value', { name: t(name) }))}
        </TextButton>
      )}
    </Box>
  )
}

export default InputAddDropdown
